import React, { useEffect, useState } from 'react';
import api from '../../api';
import { Users, CheckCircle, Clock, TrendingUp, Search, X, Check, Ban, RefreshCw, Plus, Trash2, Award, Send } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

const STATUS_CONFIG = {
  active:    { cls: 'bg-emerald-100 text-emerald-700',  label: 'Active'    },
  pending:   { cls: 'bg-amber-100 text-amber-700',      label: 'Pending'   },
  suspended: { cls: 'bg-red-100 text-red-600',          label: 'Suspended' },
  rejected:  { cls: 'bg-slate-100 text-slate-500',      label: 'Rejected'  },
};

const PROGRAM_CONFIG = {
  referral: { cls: 'bg-blue-100 text-blue-700',      label: 'Referral'  },
  reseller: { cls: 'bg-purple-100 text-purple-700',  label: 'Reseller'  },
};

const EVENT_TYPES = ['new_subscription', 'renewal', 'upgrade', 'addon'];

export default function PartnersPage() {
  const [tab, setTab] = useState('partners');
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [totalP, setTotalP] = useState(0);
  const [pageP, setPageP] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  // Payouts state
  const [payouts, setPayouts] = useState([]);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutFilterStatus, setPayoutFilterStatus] = useState('pending');
  const [rejectPayoutId, setRejectPayoutId] = useState(null);
  const [payoutRejectReason, setPayoutRejectReason] = useState('');
  const [payoutActionLoading, setPayoutActionLoading] = useState('');

  // Commission rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '', program_type: 'referral', event_type: 'new_subscription',
    calculation_type: 'percentage', percentage: '', fixed_amount: '',
    minimum_amount: '', maximum_amount: '', hold_period: 0,
  });

  // Tiers state
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [showTierForm, setShowTierForm] = useState(false);
  const [newTier, setNewTier] = useState({
    name: '', description: '', min_commissions_earned: '', min_attributed_revenue: '',
    commission_rate_multiplier: 1.0, badge_color: 'silver', benefits: '', sort_order: 0,
  });
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);

  const loadStats = () => {
    api.get('/master/partners/stats').then((res) => {
      if (res?.success) setStats(res.data);
    }).catch(() => {});
  };

  const loadPartners = (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 20 });
    if (search) params.set('search', search);
    if (filterType) params.set('program_type', filterType);
    if (filterStatus) params.set('status', filterStatus);

    api.get(`/master/partners?${params}`)
      .then((res) => {
        if (res?.success) {
          setPartners(res.data?.partners || []);
          setTotalP(res.data?.total || 0);
          setPageP(p);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadRules = () => {
    setRulesLoading(true);
    api.get('/master/partners/rules').then((res) => {
      if (res?.success) setRules(res.data || []);
    }).finally(() => setRulesLoading(false));
  };

  useEffect(() => { loadStats(); loadPartners(1); }, []);
  useEffect(() => { if (tab === 'rules') loadRules(); }, [tab]);
  useEffect(() => { if (tab === 'payouts') loadPayouts(1, payoutFilterStatus); }, [tab]);
  useEffect(() => { if (tab === 'tiers') loadTiers(); }, [tab]);

  const loadPayouts = (p = 1, status = 'pending') => {
    setPayoutsLoading(true);
    const params = new URLSearchParams({ page: p, limit: 20 });
    if (status) params.set('status', status);
    api.get(`/master/partners/payouts?${params}`)
      .then((res) => {
        if (res?.success) {
          setPayouts(res.data?.payouts || []);
          setPayoutsTotal(res.data?.total || 0);
          setPayoutsPage(p);
        }
      })
      .catch(() => {})
      .finally(() => setPayoutsLoading(false));
  };

  const doPayoutAction = async (action, payoutId) => {
    setPayoutActionLoading(`${action}-${payoutId}`);
    try {
      if (action === 'reject') {
        if (!payoutRejectReason.trim()) { toast.error('Rejection reason required'); setPayoutActionLoading(''); return; }
        await api.post(`/master/partners/payouts/${payoutId}/reject`, { reason: payoutRejectReason });
        setRejectPayoutId(null);
        setPayoutRejectReason('');
      } else {
        await api.post(`/master/partners/payouts/${payoutId}/approve`);
      }
      toast.success(`Payout ${action}d`);
      loadPayouts(payoutsPage, payoutFilterStatus);
      loadStats();
    } catch (err) {
      toast.error(err?.detail || 'Action failed');
    } finally { setPayoutActionLoading(''); }
  };

  const loadDetail = (p) => {
    setSelected(p);
    setDetailLoading(true);
    api.get(`/master/partners/${p.id}`)
      .then((res) => { if (res?.success) setSelectedDetail(res.data); })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  };

  const doAction = async (action, partnerId) => {
    setActionLoading(action);
    try {
      if (action === 'reject') {
        if (!rejectReason.trim()) { toast.error('Rejection reason required'); setActionLoading(''); return; }
        await api.post(`/master/partners/${partnerId}/reject`, { reason: rejectReason });
        setShowReject(false);
        setRejectReason('');
      } else {
        await api.post(`/master/partners/${partnerId}/${action}`);
      }
      toast.success(`Partner ${action}d successfully`);
      loadPartners(pageP);
      loadStats();
      // Refresh detail
      if (selected?.id === partnerId) loadDetail({ ...selected });
    } catch (err) {
      toast.error(err?.detail || `Action failed`);
    } finally {
      setActionLoading('');
    }
  };

  const createRule = async () => {
    try {
      await api.post('/master/partners/rules', {
        ...newRule,
        percentage: newRule.percentage ? parseFloat(newRule.percentage) : null,
        fixed_amount: newRule.fixed_amount ? parseFloat(newRule.fixed_amount) : null,
        minimum_amount: newRule.minimum_amount ? parseFloat(newRule.minimum_amount) : null,
        maximum_amount: newRule.maximum_amount ? parseFloat(newRule.maximum_amount) : null,
        hold_period: parseInt(newRule.hold_period) || 0,
      });
      toast.success('Commission rule created');
      setShowRuleForm(false);
      setNewRule({ name: '', program_type: 'referral', event_type: 'new_subscription', calculation_type: 'percentage', percentage: '', fixed_amount: '', minimum_amount: '', maximum_amount: '', hold_period: 0 });
      loadRules();
    } catch (err) {
      toast.error(err?.detail || 'Failed to create rule');
    }
  };

  const deactivateRule = async (ruleId) => {
    try {
      await api.delete(`/master/partners/rules/${ruleId}`);
      toast.success('Rule deactivated');
      loadRules();
    } catch (err) {
      toast.error(err?.detail || 'Failed');
    }
  };

  const loadTiers = () => {
    setTiersLoading(true);
    api.get('/master/partners/tiers').then((res) => {
      if (res?.success) setTiers(res.data || []);
    }).finally(() => setTiersLoading(false));
  };

  const createTier = async () => {
    if (!newTier.name.trim()) { toast.error('Tier name is required'); return; }
    try {
      await api.post('/master/partners/tiers', {
        ...newTier,
        min_commissions_earned: parseFloat(newTier.min_commissions_earned) || 0,
        min_attributed_revenue: parseFloat(newTier.min_attributed_revenue) || 0,
        commission_rate_multiplier: parseFloat(newTier.commission_rate_multiplier) || 1.0,
        sort_order: parseInt(newTier.sort_order) || 0,
        benefits: newTier.benefits
          ? newTier.benefits.split(',').map(b => b.trim()).filter(Boolean)
          : [],
      });
      toast.success('Tier created');
      setShowTierForm(false);
      setNewTier({ name: '', description: '', min_commissions_earned: '', min_attributed_revenue: '', commission_rate_multiplier: 1.0, badge_color: 'silver', benefits: '', sort_order: 0 });
      loadTiers();
    } catch (err) {
      toast.error(err?.detail || 'Failed to create tier');
    }
  };

  const deactivateTier = async (tierId) => {
    try {
      await api.delete(`/master/partners/tiers/${tierId}`);
      toast.success('Tier deactivated');
      loadTiers();
    } catch (err) {
      toast.error(err?.detail || 'Failed');
    }
  };

  const sendWeeklyReports = async () => {
    setWeeklyReportLoading(true);
    try {
      const res = await api.post('/master/partners/send-weekly-reports');
      toast.success(res?.message || 'Weekly reports sent');
    } catch (err) {
      toast.error(err?.detail || 'Failed to send reports');
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  const releaseHeldCommissions = async () => {
    setReleaseLoading(true);
    try {
      const res = await api.post('/master/partners/release-held-commissions');
      toast.success(res?.message || 'Commissions released');
      loadStats();
    } catch (err) {
      toast.error(err?.detail || 'Release failed');
    } finally {
      setReleaseLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto" data-testid="master-partners-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Partner Network</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage referral partners and resellers</p>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Total Partners', value: stats.total, icon: Users, color: 'text-slate-700', bg: 'bg-slate-50' },
            { label: 'Active', value: stats.active, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Pending Review', value: stats.pending, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Referrals', value: stats.referrals, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Total Commissions', value: fmt(stats.total_commissions), icon: TrendingUp, color: 'text-[#516EFA]', bg: 'bg-[#EDF0FF]' },
            { label: 'Held (Eligible)', value: `${stats.held_eligible_for_release || 0}/${stats.held_total || 0}`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm" data-testid={`master-partner-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="text-xs text-slate-500 font-manrope font-semibold uppercase tracking-wider mb-1">{s.label}</p>
              <p className={`text-xl font-outfit font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[
            { value: 'partners', label: 'Partners' },
            { value: 'payouts', label: 'Payouts' },
            { value: 'rules', label: 'Commission Rules' },
            { value: 'tiers', label: 'Reseller Tiers' },
          ].map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`master-partners-tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {stats?.held_eligible_for_release > 0 && (
            <button
              onClick={releaseHeldCommissions}
              disabled={releaseLoading}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="release-held-commissions-btn"
            >
              <RefreshCw className={`w-4 h-4 ${releaseLoading ? 'animate-spin' : ''}`} />
              {releaseLoading ? 'Releasing...' : `Release ${stats.held_eligible_for_release} Held`}
            </button>
          )}
          <button
            onClick={sendWeeklyReports}
            disabled={weeklyReportLoading}
            className="flex items-center gap-2 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="send-weekly-reports-btn"
          >
            <Send className="w-4 h-4" />
            {weeklyReportLoading ? 'Sending...' : 'Send Weekly Reports'}
          </button>
        </div>
      </div>

      {/* Partners tab */}
      {tab === 'partners' && (
        <div className="flex gap-5">
          {/* Partner list */}
          <div className="flex-1 min-w-0">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadPartners(1)}
                  placeholder="Search partners..."
                  className="pl-9 border border-slate-200 rounded-lg py-2 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none w-48"
                  data-testid="master-partners-search"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setTimeout(() => loadPartners(1), 0); }}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none bg-white"
                data-testid="master-partners-type-filter"
              >
                <option value="">All Types</option>
                <option value="referral">Referral</option>
                <option value="reseller">Reseller</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setTimeout(() => loadPartners(1), 0); }}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none bg-white"
                data-testid="master-partners-status-filter"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
              <button onClick={() => loadPartners(1)} className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-2 text-sm hover:bg-slate-50 transition text-slate-600">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="master-partners-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Partner</th>
                    <th className="text-left px-4 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Earned</th>
                    <th className="text-left px-4 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">Loading...</td></tr>
                  )}
                  {!loading && partners.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No partners found</td></tr>
                  )}
                  {partners.map((p) => {
                    const st = STATUS_CONFIG[p.status] || { cls: 'bg-slate-100 text-slate-600', label: p.status };
                    const pt = PROGRAM_CONFIG[p.program_type] || { cls: 'bg-slate-100 text-slate-600', label: p.program_type };
                    return (
                      <tr
                        key={p.id}
                        className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer ${selected?.id === p.id ? 'bg-[#EDF0FF]' : ''}`}
                        onClick={() => loadDetail(p)}
                        data-testid={`partner-row-${p.id}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{p.first_name} {p.last_name}</p>
                          <p className="text-xs text-slate-400">{p.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${pt.cls}`}>{pt.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-700 tabular-nums text-xs">
                          {fmt(p.wallet?.lifetime_earned)}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {p.date_joined ? new Date(p.date_joined).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="w-80 flex-shrink-0 bg-white border border-slate-100 rounded-2xl shadow-sm p-5 space-y-4 h-fit sticky top-4" data-testid="partner-detail-panel">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 text-sm">Partner Detail</p>
                <button onClick={() => { setSelected(null); setSelectedDetail(null); }} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>

              {detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#516EFA] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : selectedDetail ? (
                <>
                  <div className="text-center py-3">
                    <div className="w-12 h-12 rounded-full bg-[#EDF0FF] flex items-center justify-center text-[#516EFA] font-bold text-lg mx-auto mb-2">
                      {selected.first_name?.[0]}{selected.last_name?.[0]}
                    </div>
                    <p className="font-semibold text-slate-900">{selected.first_name} {selected.last_name}</p>
                    <p className="text-xs text-slate-500">{selected.email}</p>
                    <div className="flex justify-center gap-2 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PROGRAM_CONFIG[selected.program_type]?.cls || ''}`}>
                        {selected.program_type}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CONFIG[selected.status]?.cls || ''}`}>
                        {selected.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-slate-50 rounded-xl p-2">
                      <p className="text-xs text-slate-500">Total Refs</p>
                      <p className="font-bold text-slate-900">{selectedDetail.stats?.total_referrals ?? 0}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-2">
                      <p className="text-xs text-slate-500">Paying Schools</p>
                      <p className="font-bold text-slate-900">{selectedDetail.stats?.paying_schools ?? 0}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-2">
                      <p className="text-xs text-slate-500">Available</p>
                      <p className="font-bold text-emerald-600">{fmt(selectedDetail.wallet?.available_balance)}</p>
                    </div>
                    <div className="bg-[#EDF0FF] rounded-xl p-2">
                      <p className="text-xs text-slate-500">Lifetime</p>
                      <p className="font-bold text-[#516EFA]">{fmt(selectedDetail.wallet?.lifetime_earned)}</p>
                    </div>
                  </div>

                  {/* Partner code & referral URL */}
                  <div className="text-xs space-y-1">
                    <p className="text-slate-500">Code: <span className="font-mono font-semibold text-slate-800">{selected.partner_code}</span></p>
                    {selected.phone && <p className="text-slate-500">Phone: {selected.phone}</p>}
                    {selected.state && <p className="text-slate-500">Location: {selected.city ? `${selected.city}, ` : ''}{selected.state}</p>}
                    {selected.program_type === 'reseller' && selected.proposed_territory && (
                      <p className="text-slate-500">Territory: {selected.proposed_territory}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    {selected.status === 'pending' && (
                      <>
                        <button
                          onClick={() => doAction('approve', selected.id)}
                          disabled={!!actionLoading}
                          className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-50"
                          data-testid="partner-approve-btn"
                        >
                          {actionLoading === 'approve' ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                          Approve
                        </button>
                        {!showReject ? (
                          <button
                            onClick={() => setShowReject(true)}
                            className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg py-2 text-sm font-medium transition-colors"
                            data-testid="partner-reject-open-btn"
                          >
                            <X className="w-4 h-4" /> Reject
                          </button>
                        ) : (
                          <div className="space-y-2">
                            <textarea
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Reason for rejection..."
                              rows={2}
                              className="w-full border border-red-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-red-400 resize-none"
                              data-testid="partner-reject-reason"
                            />
                            <div className="flex gap-2">
                              <button onClick={() => { setShowReject(false); setRejectReason(''); }} className="flex-1 border border-slate-200 rounded-lg py-1.5 text-xs text-slate-500 hover:bg-slate-50">Cancel</button>
                              <button
                                onClick={() => doAction('reject', selected.id)}
                                disabled={!!actionLoading}
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-1.5 text-xs font-medium transition-colors"
                                data-testid="partner-reject-confirm-btn"
                              >Confirm Reject</button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {selected.status === 'active' && (
                      <button
                        onClick={() => doAction('suspend', selected.id)}
                        disabled={!!actionLoading}
                        className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-50"
                        data-testid="partner-suspend-btn"
                      >
                        <Ban className="w-4 h-4" /> Suspend Account
                      </button>
                    )}
                    {(selected.status === 'suspended' || selected.status === 'rejected') && (
                      <button
                        onClick={() => doAction('activate', selected.id)}
                        disabled={!!actionLoading}
                        className="w-full flex items-center justify-center gap-2 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-50"
                        data-testid="partner-activate-btn"
                      >
                        <CheckCircle className="w-4 h-4" /> Activate
                      </button>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Payouts tab */}
      {tab === 'payouts' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {['pending', 'approved', 'rejected', ''].map((s) => (
              <button
                key={s}
                onClick={() => { setPayoutFilterStatus(s); loadPayouts(1, s); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  payoutFilterStatus === s ? 'bg-[#516EFA] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`payout-filter-${s || 'all'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="master-payouts-table">
            {payoutsLoading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
            ) : payouts.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">No payout requests found</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Partner</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Bank Details</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Date</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`payout-row-${p.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-800 text-sm">{p.partner_name}</p>
                        <p className="text-xs text-slate-400">{p.partner_email}</p>
                      </td>
                      <td className="px-5 py-3 font-semibold text-slate-900 tabular-nums">₦{Number(p.amount || 0).toLocaleString()}</td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        <p>{p.bank_name}</p>
                        <p className="font-mono">{p.account_number}</p>
                        <p className="text-slate-400">{p.account_name}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                          p.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                          : p.status === 'rejected' ? 'bg-red-100 text-red-600'
                          : 'bg-amber-100 text-amber-700'
                        }`}>{p.status}</span>
                        {p.rejection_reason && (
                          <p className="text-xs text-red-500 mt-0.5">{p.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-5 py-3">
                        {p.status === 'pending' && (
                          <div className="flex flex-col gap-1.5">
                            {rejectPayoutId === p.id ? (
                              <div className="space-y-1.5">
                                <input
                                  value={payoutRejectReason}
                                  onChange={e => setPayoutRejectReason(e.target.value)}
                                  placeholder="Rejection reason..."
                                  className="w-48 border border-red-200 rounded px-2 py-1 text-xs outline-none"
                                  data-testid="payout-reject-reason"
                                />
                                <div className="flex gap-1">
                                  <button onClick={() => { setRejectPayoutId(null); setPayoutRejectReason(''); }} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                                  <button
                                    onClick={() => doPayoutAction('reject', p.id)}
                                    disabled={!!payoutActionLoading}
                                    className="text-xs bg-red-500 text-white rounded px-2 py-1 hover:bg-red-600"
                                    data-testid={`payout-reject-confirm-${p.id}`}
                                  >Confirm</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => doPayoutAction('approve', p.id)}
                                  disabled={!!payoutActionLoading}
                                  className="flex items-center justify-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                                  data-testid={`payout-approve-${p.id}`}
                                >
                                  {payoutActionLoading === `approve-${p.id}` ? '...' : 'Approve'}
                                </button>
                                <button
                                  onClick={() => setRejectPayoutId(p.id)}
                                  className="flex items-center justify-center gap-1 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                                  data-testid={`payout-reject-${p.id}`}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Commission Rules tab */}
      {tab === 'rules' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Commission rules determine how much partners earn per event. Admin must configure all rules — no defaults are set.
            </p>
            <button
              onClick={() => setShowRuleForm(true)}
              className="flex items-center gap-1.5 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              data-testid="create-rule-btn"
            >
              <Plus className="w-4 h-4" /> New Rule
            </button>
          </div>

          {/* New rule form */}
          {showRuleForm && (
            <div className="bg-white border border-[#516EFA]/20 rounded-2xl p-5 space-y-4 shadow-sm" data-testid="new-rule-form">
              <p className="font-semibold text-slate-800 text-sm">Create Commission Rule</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Rule Name</label>
                  <input value={newRule.name} onChange={e => setNewRule(r => ({ ...r, name: e.target.value }))}
                    placeholder="e.g. Referral New Subscription 10%"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="rule-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Program Type</label>
                  <select value={newRule.program_type} onChange={e => setNewRule(r => ({ ...r, program_type: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] bg-white"
                    data-testid="rule-program-type"
                  >
                    <option value="referral">Referral</option>
                    <option value="reseller">Reseller</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Event Type</label>
                  <select value={newRule.event_type} onChange={e => setNewRule(r => ({ ...r, event_type: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] bg-white"
                    data-testid="rule-event-type"
                  >
                    {EVENT_TYPES.map(et => <option key={et} value={et}>{et.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Calculation</label>
                  <select value={newRule.calculation_type} onChange={e => setNewRule(r => ({ ...r, calculation_type: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] bg-white"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₦)</option>
                  </select>
                </div>
                <div>
                  {newRule.calculation_type === 'percentage' ? (
                    <>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Percentage (%)</label>
                      <input type="number" min="0" max="100" value={newRule.percentage} onChange={e => setNewRule(r => ({ ...r, percentage: e.target.value }))}
                        placeholder="e.g. 10"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                        data-testid="rule-percentage"
                      />
                    </>
                  ) : (
                    <>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Fixed Amount (₦)</label>
                      <input type="number" min="0" value={newRule.fixed_amount} onChange={e => setNewRule(r => ({ ...r, fixed_amount: e.target.value }))}
                        placeholder="e.g. 5000"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                        data-testid="rule-fixed-amount"
                      />
                    </>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Min Commission (₦)</label>
                  <input type="number" min="0" value={newRule.minimum_amount} onChange={e => setNewRule(r => ({ ...r, minimum_amount: e.target.value }))}
                    placeholder="Optional"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Max Commission (₦)</label>
                  <input type="number" min="0" value={newRule.maximum_amount} onChange={e => setNewRule(r => ({ ...r, maximum_amount: e.target.value }))}
                    placeholder="Optional"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Hold Period (days)</label>
                  <input type="number" min="0" value={newRule.hold_period} onChange={e => setNewRule(r => ({ ...r, hold_period: e.target.value }))}
                    placeholder="0 = instant"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowRuleForm(false)} className="border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition">Cancel</button>
                <button onClick={createRule} className="bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-testid="save-rule-btn">
                  Save Rule
                </button>
              </div>
            </div>
          )}

          {/* Rules list */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="commission-rules-table">
            {rulesLoading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Loading rules...</div>
            ) : rules.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-500 text-sm">No commission rules configured yet.</p>
                <p className="text-xs text-slate-400 mt-1">Create rules above to enable partner commissions.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Rule</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Program</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Event</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Commission</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Hold</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`rule-row-${r.id}`}>
                      <td className="px-5 py-3 font-medium text-slate-800">{r.name}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PROGRAM_CONFIG[r.program_type]?.cls || 'bg-slate-100 text-slate-600'}`}>
                          {r.program_type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600 capitalize">{r.event_type?.replace(/_/g, ' ')}</td>
                      <td className="px-5 py-3 text-xs font-semibold text-slate-800">
                        {r.calculation_type === 'percentage'
                          ? `${r.percentage}%`
                          : fmt(r.fixed_amount)}
                        {r.minimum_amount && <span className="text-slate-400 font-normal"> (min {fmt(r.minimum_amount)})</span>}
                        {r.maximum_amount && <span className="text-slate-400 font-normal"> (max {fmt(r.maximum_amount)})</span>}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">{r.hold_period ? `${r.hold_period}d` : 'Instant'}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {r.status === 'active' && (
                          <button
                            onClick={() => deactivateRule(r.id)}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                            data-testid={`deactivate-rule-${r.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Tiers tab */}
      {tab === 'tiers' && (
        <div className="space-y-5" data-testid="tiers-section">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-800">Reseller Tier Configuration</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Tiers unlock higher commission multipliers as partners hit revenue milestones. Partners qualify when they meet <strong>either</strong> threshold.
              </p>
            </div>
            <button
              onClick={() => setShowTierForm(!showTierForm)}
              className="flex items-center gap-2 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              data-testid="add-tier-btn"
            >
              <Plus className="w-4 h-4" /> Add Tier
            </button>
          </div>

          {/* Create Tier Form */}
          {showTierForm && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="tier-form">
              <h3 className="text-sm font-semibold text-slate-800">New Tier</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Tier Name *</label>
                  <input value={newTier.name} onChange={e => setNewTier(t => ({ ...t, name: e.target.value }))}
                    placeholder="e.g. Silver, Gold, Platinum"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Badge Color</label>
                  <select value={newTier.badge_color} onChange={e => setNewTier(t => ({ ...t, badge_color: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]">
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                    <option value="platinum">Platinum</option>
                    <option value="diamond">Diamond</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Min Commissions Earned (₦)</label>
                  <input type="number" min="0" value={newTier.min_commissions_earned}
                    onChange={e => setNewTier(t => ({ ...t, min_commissions_earned: e.target.value }))}
                    placeholder="0 = no threshold"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Min Attributed Revenue (₦)</label>
                  <input type="number" min="0" value={newTier.min_attributed_revenue}
                    onChange={e => setNewTier(t => ({ ...t, min_attributed_revenue: e.target.value }))}
                    placeholder="0 = no threshold"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Commission Rate Multiplier</label>
                  <input type="number" min="1" step="0.05" value={newTier.commission_rate_multiplier}
                    onChange={e => setNewTier(t => ({ ...t, commission_rate_multiplier: e.target.value }))}
                    placeholder="1.0 = no boost, 1.2 = +20%"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Sort Order (higher = higher tier)</label>
                  <input type="number" min="0" value={newTier.sort_order}
                    onChange={e => setNewTier(t => ({ ...t, sort_order: e.target.value }))}
                    placeholder="0"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-600 block mb-1">Description</label>
                  <input value={newTier.description} onChange={e => setNewTier(t => ({ ...t, description: e.target.value }))}
                    placeholder="Brief description of this tier's benefits"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-600 block mb-1">Benefits (comma-separated)</label>
                  <input value={newTier.benefits} onChange={e => setNewTier(t => ({ ...t, benefits: e.target.value }))}
                    placeholder="Priority support, Dedicated account manager, Co-marketing"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowTierForm(false)} className="border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition">Cancel</button>
                <button onClick={createTier} className="bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-testid="save-tier-btn">
                  Save Tier
                </button>
              </div>
            </div>
          )}

          {/* Tiers list */}
          <div className="space-y-3" data-testid="tiers-list">
            {tiersLoading ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center text-slate-400 text-sm">Loading tiers...</div>
            ) : tiers.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center">
                <Award className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm font-medium">No tiers configured yet</p>
                <p className="text-xs text-slate-400 mt-1">Create tiers to reward top-performing partners with higher commission multipliers.</p>
              </div>
            ) : (
              tiers.map((tier) => {
                const colorMap = {
                  silver: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
                  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
                  platinum: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
                  diamond: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
                };
                const colors = colorMap[tier.badge_color] || colorMap.silver;
                return (
                  <div key={tier.id} className={`bg-white border ${colors.border} rounded-2xl p-5 shadow-sm`} data-testid={`tier-row-${tier.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                          <Award className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 text-sm">{tier.name}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                              {tier.badge_color}
                            </span>
                            {!tier.is_active && (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-400">Inactive</span>
                            )}
                          </div>
                          {tier.description && <p className="text-xs text-slate-500 mt-0.5">{tier.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-xs flex-shrink-0">
                        <div className="text-center">
                          <p className="font-bold text-slate-800 text-sm">{tier.commission_rate_multiplier}x</p>
                          <p className="text-slate-400">Multiplier</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-slate-800 text-sm">{fmt(tier.min_commissions_earned || 0)}</p>
                          <p className="text-slate-400">Min Commissions</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-slate-800 text-sm">{fmt(tier.min_attributed_revenue || 0)}</p>
                          <p className="text-slate-400">Min Revenue</p>
                        </div>
                        {tier.is_active && (
                          <button
                            onClick={() => deactivateTier(tier.id)}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                            data-testid={`deactivate-tier-${tier.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                    {tier.benefits && tier.benefits.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {tier.benefits.map((b, i) => (
                          <span key={i} className={`inline-flex px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}>{b}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
